import type { AppDispatch } from 'store';
import i18n from 'i18n';
import { setLanguage } from './slices/languageSlice';
import {
  DEFAULT_LANGUAGE,
  normalizeLanguage,
  readStoredLanguage,
  type SupportedLanguage,
} from './languageConfig';

/**
 * The one place allowed to change the application language.
 *
 * The app keeps the current language in several stores that are read by
 * different consumers:
 *
 *   - `localStorage['language']` — seeds i18next on boot, stamps the `Culture`
 *     header on every API request.
 *   - the redux `language` slice — read by the sidebar, dropdowns and policy view.
 *   - `i18n.language` — every `t()` string, `<html lang>`, the catalog URL.
 *
 * Historically each call site wrote a different subset, so the stores drifted and
 * the UI rendered one language while the API answered in another. Route every
 * deliberate language change through `applyLanguage` and they move together.
 *
 * NOTE — why there is no global `i18n.on('languageChanged')` mirror here.
 * The public recruitment portal (/candidat) shares this i18next singleton but
 * must NOT own the employee app's language. A listener that mirrored i18next
 * back into localStorage would let the portal overwrite the employee's language
 * again, which is the exact defect this work removes. Direction and `lang` are
 * instead derived from i18next directly (see DirectionUpdater), so no mirror is
 * needed to keep them consistent.
 */
export async function applyLanguage(
  dispatch: AppDispatch,
  raw: string | null | undefined,
): Promise<SupportedLanguage | null> {
  const next = normalizeLanguage(raw);

  // Unusable value (empty, or a culture we have no catalog for). Leave every
  // store untouched rather than degrading the user to the default.
  if (!next) return null;

  // Always dispatched, even when it looks unchanged: the reducer writes redux
  // AND localStorage, so this re-aligns the two if they have drifted apart.
  dispatch(setLanguage(next));

  // Guarded separately — reloading resources is a network/parse cost, and
  // i18next can legitimately already be on the target language.
  if (i18n.language !== next) {
    await i18n.changeLanguage(next);
    await i18n.reloadResources(next);
  }

  return next;
}

/**
 * Apply a language supplied by the backend (login `culture`, impersonation).
 *
 * Distinct from `applyLanguage` only in intent: backend values are unvalidated
 * strings from an API whose casing/format is not guaranteed, so an unusable one
 * must be ignored rather than allowed to reset the user to the default.
 */
export async function syncLanguageFromBackend(
  dispatch: AppDispatch,
  culture: string | null | undefined,
): Promise<SupportedLanguage | null> {
  return applyLanguage(dispatch, culture);
}

/**
 * Apply a tenant-level default, which must never override a user's own choice.
 * Used for the pre-auth `checkdomain` config: it describes the tenant, not the
 * person, so it only applies to a browser that has no stored language yet.
 */
export async function applyTenantDefaultLanguage(
  dispatch: AppDispatch,
  tenantLanguage: string | null | undefined,
): Promise<SupportedLanguage | null> {
  if (readStoredLanguage()) return null;
  return applyLanguage(dispatch, tenantLanguage);
}

/** The language actually rendering right now. */
export const getActiveLanguage = (): SupportedLanguage =>
  normalizeLanguage(i18n.language) ?? readStoredLanguage() ?? DEFAULT_LANGUAGE;
